import { publish } from 'pub-sub';
import defer from 'lodash-es/defer';
import delay from 'lodash-es/delay';
import Queue from 'collections/Queue';
import { logSystem } from 'logger';
import WebSocket, { SendState } from './WebSocket';
import { createDefer } from './_defer';
import './eventtransport';
export var Action;
(function (Action) {
    Action[Action["PING"] = 0] = "PING";
    Action[Action["EVENT"] = 1] = "EVENT";
    Action[Action["JSONRPC"] = 2] = "JSONRPC";
    Action[Action["CDPPUSH"] = 3] = "CDPPUSH";
    Action[Action["MANHOLE"] = 4] = "MANHOLE";
    Action[Action["SESSID"] = 5] = "SESSID";
})(Action || (Action = {}));
const TIMEOUT_BASE = 15;
const TIMEOUT_STEP = [0, 0, 0, 0, 1, 2, 3, 5, 8, 13, 21, 34, 55, 89, 144, 233];
const propsmap = new WeakMap();
export class WSDispatcher {
    constructor(options) {
        let props = propsmap.get(this) || {};
        const { url } = options;
        props.url = url;
        props.queue_available = null;
        props.closing = false;
        propsmap.set(this, props);
        this._acquire_queue();
        props = propsmap.get(this);
        props.cbqueue = {};
        props.nextid = 1;
        props.msg_queue = new Queue();
        props.reopen_count = 0;
        propsmap.set(this, props);
        this._release_queue();
        props = propsmap.get(this);
        const ws = (props.ws = new WebSocket(this.url));
        propsmap.set(this, props);
        ws.on('message', this._msgdispatch.bind(this));
        ws.on('open', () => {
            propsmap.get(this).reopen_count = 0;
            this._flush_queue();
        });
        ws.on('close', () => {
            this._reopen();
        });
    }
    get props() {
        return propsmap.get(this);
    }
    get url() {
        return propsmap.get(this).url;
    }
    open(force = false) {
        const props = this.props;
        const { ws } = props;
        props.closing = false;
        propsmap.set(this, props);
        ws.open(force);
    }
    close() {
        const props = this.props;
        const { ws } = props;
        props.closing = true;
        propsmap.set(this, props);
        ws.close();
    }
    on(evtname, action) {
        const { ws } = this.props;
        return ws.on(evtname, action);
    }
    is_open() {
        const { ws } = this.props;
        return ws.is_open();
    }
    _reopen() {
        const props = this.props;
        const { closing, reopen_count } = props;
        if (closing) {
            return;
        }
        const tmo = Math.min(reopen_count, TIMEOUT_STEP.length - 1);
        const timeout = TIMEOUT_STEP[tmo] + TIMEOUT_BASE;
        props.reopen_count++;
        propsmap.set(this, props);
        setTimeout(() => {
            this.open();
        }, timeout * 1000);
    }
    _acquire_queue() {
        const props = this.props;
        props.queue_available = createDefer();
        propsmap.set(this, props);
    }
    _release_queue() {
        const props = this.props;
        props.queue_available && props.queue_available.resolve(true);
        propsmap.set(this, props);
    }
    _msgdispatch(msg) {
        const parsedmsg = JSON.parse(msg.data);
        const act = parsedmsg[0];
        switch (act) {
            case Action.PING:
                this._do_ping(parsedmsg);
                break;
            case Action.EVENT:
                this._do_event(parsedmsg);
                break;
            case Action.JSONRPC:
                this._do_jsonrpc(parsedmsg);
                break;
            case Action.CDPPUSH:
                this._do_cdppush(parsedmsg);
                break;
            case Action.MANHOLE:
                this._do_manhole(parsedmsg);
                break;
        }
    }
    _do_ping(msg) {
        const props = this.props;
        const { ping_hnd } = props;
        if (ping_hnd) {
            clearTimeout(ping_hnd);
        }
        props.ping_hnd = setTimeout(() => {
            props.ping_hnd = null;
            this.close();
            setTimeout(() => {
                props.closing = false;
                propsmap.set(this, props);
                this._reopen();
            }, 2000);
        }, 40000);
        propsmap.set(this, props);
    }
    _do_event(msg) {
        const evtobj = msg[3];
        const evt = evtobj.event;
        const args = evtobj.params;
        logSystem.debug({ msg: 'event =>', data: evtobj });
        publish(evt, args);
    }
    _do_jsonrpc(msg) {
        const qid = msg[1];
        const headers = msg[2];
        const data = JSON.parse(msg[3]);
        const { cbqueue } = this.props;
        const p = cbqueue[qid];
        if (p) {
            if (p.returnResponseObject) {
                const response = {
                    content: data,
                    headers: new Map()
                };
                Object.keys(headers).forEach((k) => {
                    response.headers.set(k.toLowerCase(), headers[k]);
                });
                p.resolve(response);
            }
            else {
                p.resolve(data);
            }
            delete cbqueue[qid];
        }
    }
    _do_cdppush(msg) {
    }
    _do_manhole(msg) {
        const data = msg[3];
        publish('manhole:response', data.message);
    }
    _add_back(message) {
        const { queue_available, msg_queue } = this.props;
        return queue_available.then(() => {
            msg_queue.enqueue(message);
        });
    }
    _send() {
        const { ws, msg_queue: mq, queue_available } = this.props;
        let timeout = 0;
        if (!queue_available.isResolved || !ws.is_open() || mq.isEmpty()) {
            return;
        }
        this._acquire_queue();
        const message = mq.peek();
        const sendokay = ws.send(JSON.stringify(message));
        if (sendokay === SendState.OKAY) {
            mq.dequeue();
        }
        this._release_queue();
        if (!mq.isEmpty() && (sendokay === SendState.OKAY || sendokay === SendState.BUFFERING)) {
            timeout = sendokay === SendState.OKAY ? 0 : 50;
            delay(() => this._send(), timeout);
        }
    }
    _flush_queue() {
        let msg;
        const { ws, queue_available } = this.props;
        queue_available &&
            queue_available.then(() => {
                this._acquire_queue();
                const sessionid = window.hcstatic.sessionid;
                if (sessionid) {
                    msg = [Action.SESSID, 0, null, { sessionid: sessionid }];
                    ws.send(JSON.stringify(msg));
                }
                this._release_queue();
                defer(() => this._send());
            });
    }
    send(message) {
        this._add_back(message).then(() => this._send());
    }
    send_ping(message) {
        this.send([Action.PING, 0, null, message]);
    }
    send_event(message) {
        logSystem.info(`event <= ${message}`);
        this.send([Action.EVENT, 0, null, message]);
    }
    send_jsonrpc(path, message, returnResponseObject = false) {
        logSystem.debug(() => {
            const loglen = Math.min(message.length, 200);
            const ellipsis = loglen < message.length ? '...' : '';
            return `${path} <= ${message.slice(0, loglen)}${ellipsis}`;
        });
        const props = this.props;
        const msg = [Action.JSONRPC, props.nextid++, path, message];
        const d = createDefer();
        d.returnResponseObject = returnResponseObject;
        props.cbqueue[msg[1]] = d;
        this.send(msg);
        propsmap.set(this, props);
        return d;
    }
    send_cdppush(message) {
        this.send([Action.CDPPUSH, 0, null, message]);
    }
    send_manhole(message) {
        this.send([Action.MANHOLE, 0, null, message + '\n']);
    }
    simulate(action) {
        const props = this.props;
        if (action === 'disconnect') {
            !props.closing && this.close();
        }
        else {
            !this.is_open() && this.open();
        }
    }
}
export default WSDispatcher;
